import { useEffect, useRef } from 'react';

/**
 * Fire `onSeen` ONCE, when the element is genuinely on screen.
 *
 * "Genuinely" is the whole point. A portfolio-wide measurement bug came from
 * counting an offer as *seen* because its component had rendered — which is
 * true of anything 6,000px below the fold, inside a closed dialog, or behind
 * `display:none`. IntersectionObserver answers the question the reports were
 * actually asking, and `threshold` keeps a one-pixel sliver from counting.
 *
 * Returns the ref to attach to the element being measured.
 */
export function useImpression<T extends HTMLElement>(
  onSeen: () => void,
  enabled = true,
  threshold = 0.5,
) {
  const ref = useRef<T | null>(null);
  const fired = useRef(false);
  // Kept in a ref so an inline arrow at the call site does not re-run the
  // effect on every render (which would re-observe and could double-fire).
  const cb = useRef(onSeen);
  cb.current = onSeen;

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || fired.current) return;
    // No IntersectionObserver (old browser, jsdom, prerender) means we cannot
    // tell whether it was visible — so we report nothing rather than guess.
    if (typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting || fired.current) continue;
          fired.current = true;
          io.disconnect();
          cb.current();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, threshold]);

  return ref;
}
